// iter570 — One-click Reconcile dialog for a single supplier.
// Shows the Total / Cash / Bank / Outstanding breakdown side-by-side
// with every bill + every payment, and lets the user attach unpaid
// credit bills to bank statement lines via the existing
// SupplierPaymentDialog flow without leaving the screen.

import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, FileText, Receipt, Banknote, AlertTriangle, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SupplierReconcileDialog({ supplier, paySummary, onClose, onOpenBankPay, onOpenPayCredit }) {
  const open = !!supplier;
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);
  const [unpaid, setUnpaid] = useState([]);

  useEffect(() => {
    if (!open || !supplier?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/suppliers/${supplier.id}/ledger`).then((r) => r.data).catch(() => null),
      api.get(`/suppliers/${supplier.id}/unpaid-invoices`).then((r) => r.data?.invoices || []).catch(() => []),
    ]).then(([lg, up]) => {
      setLedger(lg);
      setUnpaid(up);
    }).finally(() => setLoading(false));
  }, [open, supplier?.id]);

  const totals = useMemo(() => {
    if (!supplier) return { total: 0, cash: 0, bank: 0, returns: 0, credit: 0, outstanding: 0 };
    const total = Number(supplier.total_purchases || 0);
    const cash = Number(paySummary?.cash || 0);
    const bank = Number(paySummary?.bank || 0);
    // iter578 — Outstanding now also subtracts returns (supplier credit-returns
    // + bill-returns) to match current_credit formula. Without this, returns
    // produced phantom Δ gaps that flagged perfectly-reconciled suppliers as
    // "discrepant" in the dialog's health banner.
    const returns = Number(paySummary?.returns || 0);
    const credit = Number(supplier.current_credit || 0);
    const outstanding = Math.max(0, total - cash - bank - returns);
    return { total, cash, bank, returns, credit, outstanding };
  }, [supplier, paySummary]);

  const purchases = ledger?.entries?.filter((t) => t.type === 'purchase') || [];
  const payments = ledger?.entries?.filter((t) => t.type === 'payment') || [];
  // iter583 — Bill returns + supplier returns also belong in the Recent
  // activity panel since they offset what we owe the supplier.
  const returnsList = ledger?.entries?.filter((t) => t.type === 'bill_return' || t.type === 'supplier_return') || [];
  const matchHealth = Math.abs(totals.outstanding - totals.credit) < 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[88dvh] overflow-y-auto" data-testid="supplier-reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" /> Reconcile {supplier?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <Loader2 size={20} className="animate-spin" />
            <div className="text-xs">Loading bills + payments…</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats banner */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="reconcile-stats">
              <div className="p-2 rounded-lg bg-muted/40 text-center">
                <div className="text-[10px] text-muted-foreground">Total Purchases</div>
                <div className="text-sm font-bold">{fmt(totals.total)}</div>
              </div>
              <div className="p-2 rounded-lg bg-success/10 text-center">
                <div className="text-[10px] text-muted-foreground">Cash Paid</div>
                <div className="text-sm font-bold text-success">{fmt(totals.cash)}</div>
              </div>
              <div className="p-2 rounded-lg bg-info/10 text-center">
                <div className="text-[10px] text-muted-foreground">Bank Paid</div>
                <div className="text-sm font-bold text-info">{fmt(totals.bank)}</div>
              </div>
              <div className={`p-2 rounded-lg text-center ${totals.outstanding > 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                <div className="text-[10px] text-muted-foreground">Outstanding</div>
                <div className={`text-sm font-bold ${totals.outstanding > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmt(totals.outstanding)}</div>
              </div>
              <div className={`p-2 rounded-lg text-center ${totals.credit > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                <div className="text-[10px] text-muted-foreground">Current Credit</div>
                <div className={`text-sm font-bold ${totals.credit > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(totals.credit)}</div>
              </div>
            </div>

            {/* Health banner */}
            <div className={`flex items-center justify-between gap-2 p-2 rounded-lg text-xs ${matchHealth ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              <span className="flex items-center gap-1.5 font-medium">
                {matchHealth ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                {matchHealth
                  ? 'Outstanding and Current Credit reconcile cleanly.'
                  : `Discrepancy: Outstanding ${fmt(totals.outstanding)} vs Credit ${fmt(totals.credit)} (Δ ${fmt(Math.abs(totals.outstanding - totals.credit))}). Likely an unbooked payment or a bill paid without linking to a credit invoice.`}
              </span>
              {!matchHealth && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
                  onClick={() => onOpenBankPay && onOpenBankPay(supplier)}
                  data-testid="reconcile-quickfix-bank"
                  title="Open the Pay-from-Bank dialog to attach a bank statement line to one of the unpaid bills"
                >
                  <Banknote size={12} className="mr-1" /> Attach from Bank
                </Button>
              )}
            </div>

            {/* Action bar */}
            <div className="flex flex-wrap gap-2">
              {totals.credit > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-info/40 text-info hover:bg-info/10"
                  onClick={() => onOpenPayCredit && onOpenPayCredit(supplier)}
                  data-testid="reconcile-action-pay-credit"
                >
                  <Receipt size={12} className="mr-1" /> Pay Credit ({fmt(totals.credit)})
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                onClick={() => onOpenBankPay && onOpenBankPay(supplier)}
                data-testid="reconcile-action-pay-bank"
              >
                <Banknote size={12} className="mr-1" /> Pay from Bank
              </Button>
            </div>

            {/* Two columns: Unpaid bills (left) + Recent activity (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Unpaid bills */}
              <div className="border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                    <AlertTriangle size={12} /> Unpaid credit bills
                  </span>
                  <Badge variant="secondary" className="font-normal" data-testid="reconcile-unpaid-count">{unpaid.length}</Badge>
                </div>
                <div className="max-h-[260px] overflow-y-auto">
                  {unpaid.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">
                      <CheckCircle2 size={18} className="mx-auto mb-2 text-emerald-500" />
                      No unpaid bills — everything is settled.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Description</th>
                            <th className="text-right p-2 font-medium">Total</th>
                            <th className="text-right p-2 font-medium">Paid</th>
                            <th className="text-right p-2 font-medium">Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unpaid.map((inv, i) => (
                            <tr key={inv.id || i} className="border-t hover:bg-muted/30" data-testid={`reconcile-unpaid-row-${i}`}>
                              <td className="p-2 whitespace-nowrap font-mono text-[10px]">{(inv.date || '').slice(0, 10)}</td>
                              <td className="p-2 truncate max-w-[160px]" title={inv.description}>{inv.description || '—'}</td>
                              <td className="p-2 text-right font-mono">{fmt(inv.amount)}</td>
                              <td className="p-2 text-right font-mono text-emerald-700">{fmt(inv.paid_so_far)}</td>
                              <td className="p-2 text-right font-mono font-bold text-rose-700">{fmt(inv.remaining || (inv.amount - (inv.paid_so_far || 0)))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent activity */}
              <div className="border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-800 flex items-center gap-1.5">
                    <FileText size={12} /> Recent activity
                  </span>
                  <Badge variant="secondary" className="font-normal" data-testid="reconcile-activity-count">{purchases.length + payments.length + returnsList.length}</Badge>
                </div>
                <div className="max-h-[260px] overflow-y-auto">
                  {(purchases.length + payments.length + returnsList.length) === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">No transactions yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Type</th>
                            <th className="text-left p-2 font-medium">Description</th>
                            <th className="text-right p-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* iter583 — merge purchases + payments + returns, sorted by
                              date desc. Ledger entries use {debit, credit}; pick
                              whichever is non-zero for the amount column. */}
                          {[
                            ...purchases.map((p) => ({ ...p, _kind: 'purchase' })),
                            ...payments.map((p) => ({ ...p, _kind: 'payment' })),
                            ...returnsList.map((p) => ({ ...p, _kind: p.type === 'bill_return' ? 'bill_return' : 'supplier_return' })),
                          ]
                            .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                            .slice(0, 50)
                            .map((row, i) => {
                              const amount = row._kind === 'purchase' ? Number(row.debit || 0) : Number(row.credit || 0);
                              const isReversed = !!row.reversed_at;
                              const isReturn = row._kind === 'bill_return' || row._kind === 'supplier_return';
                              const badgeCls = row._kind === 'purchase'
                                ? 'bg-orange-100 text-orange-800'
                                : isReturn
                                  ? 'bg-rose-100 text-rose-800'
                                  : isReversed
                                    ? 'bg-stone-200 text-stone-500 line-through'
                                    : 'bg-emerald-100 text-emerald-800';
                              const amountCls = row._kind === 'purchase'
                                ? 'text-orange-700'
                                : isReturn
                                  ? 'text-rose-700'
                                  : isReversed
                                    ? 'text-stone-400 line-through'
                                    : 'text-emerald-700';
                              const badgeLabel = row._kind === 'purchase'
                                ? 'Bill'
                                : row._kind === 'bill_return'
                                  ? 'Bill Return'
                                  : row._kind === 'supplier_return'
                                    ? 'Return'
                                    : (row.payment_mode || 'Pay');
                              return (
                                <tr key={row.id || i} className="border-t hover:bg-muted/30" data-testid={`reconcile-activity-row-${i}`}>
                                  <td className="p-2 whitespace-nowrap font-mono text-[10px]">{(row.date || '').slice(0, 10)}</td>
                                  <td className="p-2">
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${badgeCls}`} title={isReversed ? `Reversed: ${row.reverse_reason || ''}` : undefined}>
                                      {badgeLabel}
                                    </span>
                                  </td>
                                  <td className="p-2 truncate max-w-[160px]" title={row.description}>{row.description || '—'}</td>
                                  <td className={`p-2 text-right font-mono ${amountCls}`}>
                                    {row._kind !== 'purchase' ? '−' : ''}{fmt(amount)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reconcile-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
